import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Search } from "lucide-react";
import { ArchiveViewChips } from "@/components/nds/ArchiveView";
import { BulkArchiveBar, archiveApi } from "@/components/nds/BulkArchiveBar";
import { ClearedMistakesList } from "@/components/nds/ClearMistake";

const when = (v) => (v ? String(v).slice(0, 10) : "");

export function KindList({ kinds, kind, setKind }) {
  const groups = [...new Set(kinds.map((k) => k.group))];
  return (
    <nav data-testid="archive-kinds" className="space-y-4">
      {groups.map((g) => (
        <div key={g}>
          <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-white/40 mb-1">{g}</div>
          {kinds.filter((k) => k.group === g).map((k) => (
            <button key={k.kind} type="button" onClick={() => setKind(k.kind)} data-testid={`archive-kind-${k.kind}`}
              className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-sm text-sm text-left transition-colors ${kind === k.kind ? "bg-[#d4a017]/15 text-[#d4a017]" : "text-white/75 hover:bg-white/5"}`}>
              <span className="truncate">{k.label}</span>
              <span className="font-mono text-[10px] text-white/45">{k.archived}</span>
            </button>
          ))}
        </div>
      ))}
    </nav>
  );
}

export function Items({ kind, meta, onChanged, residentId = "" }) {
  const [view, setView] = useState(residentId ? "all" : "archived");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const load = useCallback((skip = 0) => archiveApi.get(`/items/${kind}`, { view, q, skip, limit: 100, resident_id: residentId })
    .then((d) => setData((prev) => (skip && prev ? { ...d, items: [...prev.items, ...d.items] } : d))), [kind, view, q, residentId]);
  useEffect(() => { setSel([]); setData(null); const t = setTimeout(() => load(), 250); return () => clearTimeout(t); }, [load]);
  const k = meta.kinds.find((x) => x.kind === kind);
  const items = data?.items || [];
  const archivedIds = items.filter((x) => x.archived).map((x) => x.id);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allOn = items.length > 0 && items.every((x) => sel.includes(x.id));
  const done = () => { setSel([]); load(); onChanged(); };
  return (
    <section data-testid="archive-items" className="space-y-3 min-w-0">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base md:text-lg font-semibold">{k?.label}</h2>
        <ArchiveViewChips prefix="archive-view" view={view} setView={setView} counts={k} size="sm" />
        <label className="relative ml-auto">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-white/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" data-testid="archive-search"
            className="pl-7 pr-2 py-1.5 w-56 bg-black/40 border border-white/10 rounded-sm text-sm focus:outline-none focus:border-[#d4a017]/60" />
        </label>
      </div>
      <BulkArchiveBar kind={kind} label={k?.label.toLowerCase()} selected={sel} selectedArchived={archivedIds} reasons={meta.reasons}
        canDelete={meta.can_delete} onDone={done} testid="archive-bulk" />
      {!data ? <div className="flex items-center gap-2 text-white/50 text-sm p-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div> : (
        <div className="border border-white/10 rounded-sm overflow-hidden">
          <div className="flex items-center gap-3 px-3 py-2 bg-white/[0.03] font-mono text-[9px] tracking-[0.2em] uppercase text-white/50">
            <input type="checkbox" checked={allOn} onChange={() => setSel(allOn ? [] : items.map((x) => x.id))} data-testid="archive-select-all" aria-label="Select all" />
            <span className="flex-1">{data.total} item{data.total === 1 ? "" : "s"}</span>
          </div>
          <ul className="divide-y divide-white/5">
            {items.map((x) => (
              <li key={x.id} data-testid={`archive-item-${x.id}`} className="flex items-start gap-3 px-3 py-2.5 text-sm hover:bg-white/[0.02]">
                <input type="checkbox" className="mt-1" checked={sel.includes(x.id)} onChange={() => toggle(x.id)} data-testid={`archive-check-${x.id}`} aria-label="Select" />
                <div className="flex-1 min-w-0">
                  <div className="text-white/90 truncate">{x.title}{x.resident && !residentId && <span className="text-white/50"> · {x.resident}</span>}</div>
                  {x.subtitle && <div className="text-xs text-white/50 truncate">{x.subtitle}</div>}
                  {x.legacy && <div className="text-xs text-amber-200/80 truncate" data-testid={`archive-legacy-${x.id}`}><span className="mr-1.5 px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-200 font-mono text-[9px] uppercase tracking-wider">Legacy</span>{x.note}</div>}
                </div>
                {x.archived && <span data-testid={`archive-item-archived-${x.id}`} className="px-2 py-0.5 rounded-full border border-amber-400/40 text-amber-300 font-mono text-[9px] tracking-[0.18em] uppercase">Archived {when(x.archived_at)}</span>}
                <span className="font-mono text-[10px] text-white/40 whitespace-nowrap">{when(x.date)}</span>
              </li>
            ))}
            {!items.length && <li data-testid="archive-empty" className="p-8 text-center text-sm text-white/50">Nothing here.</li>}
          </ul>
          {items.length < data.total && (
            <button type="button" onClick={() => load(items.length)} data-testid="archive-more" className="w-full py-2 font-mono text-[10px] tracking-[0.2em] uppercase text-[#d4a017] hover:bg-white/[0.03]">Load more</button>
          )}
        </div>
      )}
    </section>
  );
}

export default function ArchiveCenterPage() {
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [kind, setKind] = useState(() => localStorage.getItem("tea_archive_kind") || "partners");
  const loadMeta = () => archiveApi.get("/kinds").then((d) => {
    const kinds = d.kinds.filter((k) => k.group === "Everything else" || k.kind === "tasks");
    setMeta({ ...d, kinds });
    if (!kinds.some((k) => k.kind === kind)) setKind(kinds[0]?.kind);
  }).catch((e) => setError(e?.response?.data?.detail || "Couldn't load the Archive Center"));
  useEffect(() => { loadMeta(); }, []);
  const pick = (k) => { localStorage.setItem("tea_archive_kind", k); setKind(k); };
  return (
    <main data-testid="archive-center-page" className="min-h-screen bg-[#0b0814] text-white px-6 py-10">
      <div className="max-w-6xl mx-auto space-y-8">
        <Link to="/admin" data-testid="archive-center-back" className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.22em] uppercase text-[#d4a017]"><ArrowLeft className="h-3.5 w-3.5" /> Admin OS</Link>
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#d4a017]">System</div>
          <h1 className="text-4xl sm:text-5xl font-semibold mt-1">Archive Center</h1>
          <p className="text-white/60 mt-2 text-sm">Records not tied to a resident: partners, vendors, properties, templates and staff tasks. Resident information is archived from Campus Residents → the resident → Archive tab. Super admins can permanently delete archived items.</p>
        </div>
        {error && <p data-testid="archive-center-error" className="text-red-300">{error}</p>}
        {meta && (
          <div className="grid md:grid-cols-[220px_1fr] gap-8">
            <KindList kinds={meta.kinds} kind={kind} setKind={pick} />
            <Items key={kind} kind={kind} meta={meta} onChanged={loadMeta} />
          </div>
        )}
        <ClearedMistakesList />
      </div>
    </main>
  );
}
